import React, { useRef, type RefObject } from "react";
import CutoutMaskContainer from "../CutoutMaskContainer";
import Glassmorphism from "../Glassmorphism";
import TextToSpeechButton from "../common/TextToSpeechButton/TextToSpeechButton";
import { useFitText } from "../../hooks/useFitText";
import { AniWaveform } from "../chat/AniWaveform";

interface InstanceUpdateProps {
  instanceData:
    | {
        ani_update?: string;
        // Add other properties of instanceData if used elsewhere in the original component
      }
    | null
    | undefined;
}

const InstanceUpdate: React.FC<InstanceUpdateProps> = ({ instanceData }) => {
  const textRef = useRef<HTMLDivElement>(null);

  useFitText(textRef as RefObject<HTMLElement>, [instanceData?.ani_update]);

  return (
    <div className="relative h-full min-h-72 w-full rounded-2xl overflow-hidden">
      <CutoutMaskContainer
        showCutout={!!instanceData?.ani_update}
        outerCurveRadius={14}
        innerCurveRadius={38}
        verticalConnectorLength={0}
        horizontalConnectorLength={0}
        simpleCornerRadius={17}
        morphProgress={0}
        animationDurationMs={100}
        className="w-full h-full"
        cutoutOffset={{ x: 0, y: 0 }}
      >
        {/* This div is the positioning context for Glassmorphism and the text overlay */}
        <div className="relative w-full h-full text-center bg-slate-100 dark:bg-slate-700">
          <div className="absolute inset-0 z-0 overflow-hidden ">
            <div className="ani-blob1 absolute w-64 h-64 bg-blue-500/20 rounded-full blur-3xl -left-16 -top-16"></div>
            <div className="ani-blob2 absolute w-72 h-72 bg-purple-500/20 rounded-full blur-3xl -right-20 top-1/2"></div>
            <div className="ani-blob3 absolute w-56 h-56 bg-pink-500/20 rounded-full blur-3xl left-1/2 -bottom-16"></div>
          </div>
          <div className="absolute top-0 left-12 right-12 h-[1px]">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,transparent_0%,#a7bffa_40%,#c084fc_50%,#a7bffa_60%,transparent_100%)] transition-opacity duration-300 ease-in-out opacity-100"></div>
          </div>
          <div className="absolute -top-10 left-0 right-0">
            <AniWaveform
              isActive={true}
              message={instanceData?.ani_update}
              baseAmplitude={8}
              repeat={true}
              maxDetailAmplitude={12}
              pulseAmplitudeMultiplier={24}
              // Sets background wave energy when Ani is "thinking" (no message).
              thinkingExcitement={0.2}
              // Sets background wave energy when Ani is "speaking" (has a message).
              speakingExcitement={1.0}
              // Sets the opacity of the blue and pink background waves (0.0 to 1.0).
              backgroundWaveOpacity={0.8}
            />
          </div>
          {/* This div overlays Glassmorphism and centers the text content */}
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-4 py-8">
            {instanceData?.ani_update ? (
              <div
                ref={textRef}
                className="text-lg font-medium px-4"
                title="Ani Update"
              >
                {instanceData.ani_update}
              </div>
            ) : (
              <div className="text-slate-500 dark:text-slate-400">
                No update available.
              </div>
            )}
          </div>
        </div>
      </CutoutMaskContainer>

      {/* TTS Button positioned in the notch area */}
      {instanceData?.ani_update && (
        <div className="absolute z-30" style={{ bottom: "2px", right: "2px" }}>
          <TextToSpeechButton textToSpeak={instanceData.ani_update} />
        </div>
      )}
    </div>
  );
};

export default InstanceUpdate;
